import { useQuery } from '@tanstack/react-query'
import { apiBaseUrl } from 'constants/apiBaseUrl'
import { getHeaders } from '@/core/getHeaders'
import { OLSModelVersion } from '@otw/models/ols'

interface UseOLSVersionsOptions {
  categoryId: string | null
  enabled?: boolean
}

/**
 * Hook to fetch version history for a specific OLS category.
 */
export const useOLSVersions = ({ categoryId, enabled = true }: UseOLSVersionsOptions) => {
  const queryKey = ['ols_versions', categoryId]

  const {
    data: versions,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<OLSModelVersion[]>({
    queryKey,
    queryFn: async () => {
      if (!categoryId) return []

      const headers = await getHeaders()
      const response = await fetch(
        `${apiBaseUrl}/portal/pricing/ols/categories/${encodeURIComponent(categoryId)}/versions`,
        {
          method: 'GET',
          headers,
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch versions')
      }

      const result = await response.json()
      return result.versions as OLSModelVersion[]
    },
    enabled: enabled && !!categoryId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  })

  // Get active version
  const activeVersion = versions?.find(v => v.status === 'active')

  // Get candidate version
  const candidateVersion = versions?.find(v => v.status === 'candidate')

  // Get archived versions
  const archivedVersions = versions?.filter(v => v.status === 'archived') || []

  return {
    versions: versions || [],
    activeVersion,
    candidateVersion,
    archivedVersions,
    isLoading,
    isError,
    error,
    refetch,
  }
}
